import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import { i18n } from 'i18n';

interface CreateScenarioDialogProps {
  open: boolean;
  sourceScenarioId: string | null;
  sourceLabel: string;
  onClose: () => void;
  onConfirm: (sourceScenarioId: string | null, name: string, cloneRecipes: boolean, clonePours: boolean) => Promise<void>;
}

const MAX_NAME_LENGTH = 100;

const CreateScenarioDialog = ({ open, sourceScenarioId, sourceLabel, onClose, onConfirm }: CreateScenarioDialogProps) => {
  const [name, setName] = React.useState<string>('');
  const [cloneRecipes, setCloneRecipes] = React.useState<boolean>(true);
  const [clonePours, setClonePours] = React.useState<boolean>(true);
  const [submitting, setSubmitting] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);

  const trimmedName = name.trim();
  const isValid = trimmedName.length > 0 && trimmedName.length <= MAX_NAME_LENGTH;
  // Pours reference recipes, so they can only be cloned when recipes are too.
  const effectiveClonePours = cloneRecipes && clonePours;

  const handleClose = () => {
    if (submitting) return;
    setName('');
    setError(null);
    onClose();
  };

  const handleConfirm = async () => {
    if (!isValid) return;
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(sourceScenarioId, trimmedName, cloneRecipes, effectiveClonePours);
      setName('');
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : i18n._('Failed to clone scenario.');
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  React.useEffect(() => {
    if (open) {
      setName('');
      setCloneRecipes(true);
      setClonePours(true);
      setError(null);
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{`${i18n._('Clone')} ${sourceLabel} ${i18n._('to new scenario')}`}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          required
          fullWidth
          margin="dense"
          label={i18n._('Scenario name')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          inputProps={{ maxLength: MAX_NAME_LENGTH }}
          helperText={`${trimmedName.length}/${MAX_NAME_LENGTH} ${i18n._('characters')}`}
          disabled={submitting}
        />
        <FormGroup sx={{ mt: 1 }}>
          <FormControlLabel
            control={(
              <Checkbox
                checked={cloneRecipes}
                onChange={(e) => setCloneRecipes(e.target.checked)}
                disabled={submitting}
              />
            )}
            label={i18n._('Clone recipes')}
          />
          <FormControlLabel
            control={(
              <Checkbox
                checked={effectiveClonePours}
                onChange={(e) => setClonePours(e.target.checked)}
                disabled={submitting || !cloneRecipes}
              />
            )}
            label={i18n._('Clone pours')}
          />
        </FormGroup>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleClose} disabled={submitting}>{i18n._('Cancel')}</Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!isValid || submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {i18n._('Clone')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateScenarioDialog;
